// For google maps api
import { Controller } from "@hotwired/stimulus"
import * as myMap from "./maps"
import {defaultMapOptions, resolveMapTypeId, layerManager} from "./maps"
import { stopLinkBeingFollowed } from "./maps"
import { mapInfoPanel } from "./map_info_panel"

// this.map is saved as a global
export default class extends Controller {
  static targets = ['map', 'droneAaz', 'toggleDroneAazs', 'craneAaz', 'toggleCraneAazs', 'toggleRestrictedAirspace', 'toggleCraneZones', 'infoPanel', 'infoPanelContent' ];

  // Read tab-specific camera defaults (zoom/mapType) from DOM data attributes
  getTabCameraDefaults(tabId) {
    const tabPane = document.getElementById(tabId);
    const fallbackZoom = parseInt(this.data.get('defaultZoom')) || 12;
    const fallbackMapType = this.data.get('defaultMapType') || 'roadmap';
    if (!tabPane) return { zoom: fallbackZoom, mapType: resolveMapTypeId(fallbackMapType) };
    const zoom = parseInt(tabPane.dataset.defaultZoom) || fallbackZoom;
    const mapType = resolveMapTypeId(tabPane.dataset.defaultMapType || fallbackMapType);
    return { zoom, mapType };
  }

  // Read tab-specific layer/application defaults from DOM data attributes
  getTabDefaultsFromDOM(tabId) {
    const tabPane = document.getElementById(tabId);
    if (!tabPane) return null;

    const layersStr = tabPane.dataset.defaultLayers || '';
    const layers = layersStr.split(',').map(s => s.trim()).filter(Boolean);
    const appsStr = tabPane.dataset.defaultApplications || '';
    const applications = appsStr.split(',').map(s => s.trim()).filter(Boolean);

    return { layers, applications };
  }

  activateShape(appId) { // Highlights shape on map
    console.log('Map shape activated for appId:', appId)
    const mapShape = this.mapShapes[appId];
    if (!mapShape) return; // shapes load async, and no-shape forms never get one
    const card = document.getElementById(`card-${appId}`);

    // Handle single shape
    if (mapShape['shape']) {
      myMap.highlightMapShape(card, mapShape['shape']);
    }
    // Handle multiple shapes
    if (mapShape['shapes']) {
      mapShape['shapes'].forEach(shape => myMap.highlightMapShape(card, shape));
    }
  }

  deactivateShape(appId) {
    console.log('Map shape deactivated for appId:', appId)
    const mapShape = this.mapShapes[appId];
    if (!mapShape) return; // shapes load async, and no-shape forms never get one
    const card = document.getElementById(`card-${appId}`);

    // Handle single shape
    if (mapShape['shape']) {
      myMap.highlightMapShape(card, mapShape['shape']);
    }
    // Handle multiple shapes
    if (mapShape['shapes']) {
      mapShape['shapes'].forEach(shape => myMap.highlightMapShape(card, shape));
    }
  }

  // Use centralized toggle method
  async toggleLayer(event) {
    await layerManager.handleToggleLayer(event);
  }

  // Toggle application layers (flights, cranes, wip, show all)
  toggleApplicationLayer(event) {
    const layer = event.target.dataset.layer;
    const isChecked = event.target.checked;

    // console.log('Toggle application layer:', layer, 'checked:', isChecked);

    if (layer === 'all') {
      this.toggleAllApplications(isChecked);
    } else {
      this.toggleApplicationType(layer, isChecked);
    }
  }

  toggleAllApplications(visible) {
    // Update individual checkboxes
    const applicationCheckboxes = ['flights-toggle', 'cranes-toggle', 'wip-toggle'];
    applicationCheckboxes.forEach(id => {
      const checkbox = document.getElementById(id);
      if (checkbox) {
        checkbox.checked = visible;
      }
    });

    // Show/hide application shapes
    for (let appId in this.mapShapes) {
      const card = this.mapShapes[appId].card;
      this.setApplicationVisibility(appId, this.shouldShowShape(card, visible));
    }
  }

  toggleApplicationType(applicationType, visible) {
    // If turning off an individual layer, uncheck "show all"
    if (!visible) {
      const showAllCheckbox = document.getElementById('show-all-toggle');
      if (showAllCheckbox) {
        showAllCheckbox.checked = false;
      }
    }

    // Check if all individual layers are now checked, if so check "show all"
    if (visible) {
      const allChecked = this.areAllApplicationLayersChecked();
      const showAllCheckbox = document.getElementById('show-all-toggle');
      if (showAllCheckbox && allChecked) {
        showAllCheckbox.checked = true;
      }
    }

    // Show/hide shapes for this application type
    for (let appId in this.mapShapes) {
      const mapShape = this.mapShapes[appId];
      const card = mapShape.card;
      const cardAppType = this.getApplicationTypeFromCard(card);

      if (cardAppType === applicationType) {
        this.setApplicationVisibility(appId, this.shouldShowShape(card, visible));
      }
    }
  }

  areAllApplicationLayersChecked() {
    const applicationCheckboxes = ['flights-toggle', 'cranes-toggle', 'wip-toggle'];
    const existingCheckboxes = applicationCheckboxes
      .map(id => document.getElementById(id))
      .filter(checkbox => checkbox !== null);

    // If no checkboxes exist, return false (nothing to check)
    if (existingCheckboxes.length === 0) return false;

    // All EXISTING checkboxes must be checked
    return existingCheckboxes.every(checkbox => checkbox.checked);
  }

  getApplicationTypeFromCard(card) {
    const appType = card.dataset.appType;
    // Map the app types to our layer names
    if (appType === 'flightApplication') return 'flights';
    if (appType === 'craneApplication') return 'cranes';
    if (appType === 'wipApplication') return 'wip';
    return null;
  }

  setApplicationVisibility(appId, visible) {
    const mapShape = this.mapShapes[appId];
    if (!mapShape) return;

    if (mapShape['pin']) {
      mapShape['pin'].setVisible(visible);
    }
    if (mapShape['shape']) {
      mapShape['shape'].setVisible(visible);
    }
    if (mapShape['shapes']) {
      mapShape['shapes'].forEach(shape => shape.setVisible(visible));
    }
  }

  // Determines if a shape should be visible based on current filter state
  shouldShowShape(card, appTypeVisible) {
    if (!appTypeVisible) return false;

    const isMultiSite = this.data.get('multiSite') === 'true';
    if (!isMultiSite) return true;

    const allLocationsCheckbox = document.getElementById('all-locations-toggle');
    if (allLocationsCheckbox && allLocationsCheckbox.checked) return true;

    const activeLocationId = this.getActiveLocationId();
    if (!activeLocationId) return true;  // No active location = show all

    return card.dataset.locationId === activeLocationId;
  }

  async resetMap(event) {
    stopLinkBeingFollowed(event)

    // Close the map layer info panel
    this.closeInfoPanel();

    // Reset "All Locations" checkbox
    const allLocationsCheckbox = document.getElementById('all-locations-toggle');
    if (allLocationsCheckbox) allLocationsCheckbox.checked = false;

    const activeMainTab = document.querySelector('.tab-pane.active[id$="-container"]')
    if (activeMainTab) {
      const activeSubTab = activeMainTab.querySelector('.nav-link.active[data-location-id]');
      const { lat: mapCentreLat, lon: mapCentreLon, zoom } = this.resolveMapCentre(activeSubTab, activeMainTab);
      await this.applyTabDefaults(activeMainTab.id, mapCentreLat, mapCentreLon, zoom)
    } else { // Fallback to default settings
      const { lat: mapCentreLat, lon: mapCentreLon, zoom } = this.resolveMapCentre();
      const cam = this.getTabCameraDefaults(null);
      this.map.setOptions(defaultMapOptions(mapCentreLat, mapCentreLon, zoom, cam.mapType))
    }
  }

  setInfoWindowContent(card, infowindow, pin) {
    // nb card.dataset.appStatus is unused because status pill formatting only available in ruby - would have to re-write it in js
    infowindow.close();
    let startText = `<h2 class="small mb-1">${card.dataset.appRef}</h2>`
    let endText = `<div class="small">${card.dataset.appDates}</div>`
    let middleText = ""
    if (card.dataset.appRole == 'viewer') { // do not provide dynamic link to viewers
      middleText = `<h4>${card.dataset.appName}</h4>`
    } else {
      middleText = `<h4><a href=${card.dataset.appLink}>${card.dataset.appName}</a></h4>`
    }
    infowindow.setContent(startText + middleText + endText);
    infowindow.open({ map: this.map, anchor: pin, shouldFocus: true });
  }

  setupMapAndCards() {
    // console.log('=== setupMapAndCards START ===')
    // let result = {}
    let cards = document.getElementsByClassName('card');
    // console.log('setupMapAndCards: Found', cards.length, 'cards')
    // console.log('setupMapAndCards: Card IDs found:', Array.from(cards).map(card => card.dataset.appId))

    for (let card of cards) { // set up the shapes but do not draw
      // console.log('setupMapAndCards: Processing card', card.dataset.appId)
      this.addShapeButDoNotDraw(card.dataset.appId)
    }
    // console.log('setupMapAndCards: All shapes added to mapShapes:', Object.keys(this.mapShapes))

    for (let card of cards) { // highlight any cards that are activated
      if (card.dataset.activated === 'true') {
        // console.log('setupMapAndCards: Activating card', card.dataset.appId)
        card.classList.add('card-flight-activated')
        this.activateShape(card.dataset.appId)
      }
    }
    // console.log('=== setupMapAndCards END ===')
    // Don't draw all shapes here - let applyTabDefaults or reload MapShapes handle initial visibility
    // The shapes are created but not shown until the other code sets the correct visibility
  }

  reloadMapShapes() {
    // console.log('=== RELOAD MAP SHAPES START ===')
    // console.log('Current timestamp:', new Date().toISOString())

    // Log current cards before destroying shapes
    const currentCards = document.getElementsByClassName('card');
    // console.log('Current cards count before reload:', currentCards.length)
    // console.log('Current card IDs:', Array.from(currentCards).map(card => card.dataset.appId))

    // Log current map shapes before destroying
    // console.log('Current mapShapes keys before reload:', Object.keys(this.mapShapes))

    // FIXED: Properly destroy old shapes instead of just hiding them
    this.destroyAllMapShapes()
    // console.log('All map shapes destroyed')

    this.setupMapAndCards()
    // console.log('setupMapAndCards completed')

    // Log cards and shapes after setup
    const cardsAfterSetup = document.getElementsByClassName('card');
    console.log('Cards count after setup:', cardsAfterSetup.length)
    // console.log('Card IDs after setup:', Array.from(cardsAfterSetup).map(card => card.dataset.appId))
    // console.log('MapShapes keys after setup:', Object.keys(this.mapShapes))

    // Restore the previously visible application types
    const applicationTypes = ['flights', 'cranes', 'wip'];
    applicationTypes.forEach(appType => {
      const checkbox = document.getElementById(`${appType}-toggle`);
      if (checkbox && checkbox.checked) {
          this.toggleApplicationType(appType, true);
      }
    });

    // For multi-site, re-apply location filtering after reload
    if (this.data.get('multiSite') === 'true') {
      const allLocationsCheckbox = document.getElementById('all-locations-toggle');
      if (allLocationsCheckbox && allLocationsCheckbox.checked) {
        this.showAllLocationApplications();
      } else {
        this.filterApplicationsToActiveLocation();
      }
    }
  }

  addShapeButDoNotDraw(appId) {
    // console.log('Adding map shape: ', appId)
    let card = document.getElementById(`card-${appId}`);
    let polyPath = card.dataset.polygon; // can't use data.get because it is not unique to controller
    let circlePath = card.dataset.circleCentre;
    let circleRadius = card.dataset.circleRadius;
    let multipleShapes = card.dataset.multipleShapes;
    let applicationType = card.dataset.appType;
    let _this = this;

    // Handle multiple shapes
    if (multipleShapes && multipleShapes.trim() !== '') {
        // console.log('multipleShapes present');
        // console.log('multipleShapes', multipleShapes)
        try {
            const shapesData = JSON.parse(multipleShapes);
            if (shapesData && shapesData.length > 0) {
                let shapes = [];
                let pin = null;

                shapesData.forEach((shapeData, index) => {
                    let shape;
                    if (shapeData.type === 'polygon') {
                        shape = myMap.drawPolygonFromPath(this.map, shapeData.data, false);
                    } else if (shapeData.type === 'circle') {
                        shape = myMap.drawCircleFromPath(this.map, shapeData.data.center, shapeData.data.radius, false);
                    }

                    if (shape) {
                        shapes.push(shape);

                        // Only create pin for first shape
                        if (index === 0) {
                            pin = myMap.addPinToShape(this.map, shape, applicationType);
                            pin.addListener('mouseover', function () {
                                shapes.forEach(s => myMap.mouseover(card, s, pin));
                            });
                            pin.addListener('mouseout', function () {
                                shapes.forEach(s => myMap.mouseout(card, s, pin));
                            });
                            pin.addListener('click', function () {
                                _this.setInfoWindowContent(card, _this.infowindow, pin)
                            });
                        }
                    }
                });

                // Card event listeners
                card.addEventListener('mouseover', function () {
                    shapes.forEach(s => myMap.mouseover(card, s, pin));
                });
                card.addEventListener('mouseout', function () {
                    shapes.forEach(s => myMap.mouseout(card, s, pin));
                });

                // Zoom to fit all shapes
                const zoomLink = card.querySelector('.zoom-link');
                if (zoomLink) {
                    zoomLink.addEventListener('click', function (event) {
                        stopLinkBeingFollowed(event);
                        const bounds = new google.maps.LatLngBounds();
                        shapes.forEach(shape => {
                            if (shape.getBounds) {
                                bounds.union(shape.getBounds());
                            }
                        });
                        _this.map.fitBounds(bounds);
                    }, false);
                }

                _this.mapShapes[appId] = {'pin': pin, 'shapes': shapes, 'card': card}
            }
        } catch (error) {
            console.error('Error parsing multiple shapes:', error);
        }
    } else if (polyPath || circlePath) { // single shape so use old method to plot shape and show pin
        // console.log('polyPath', polyPath)
        // console.log('circlePath', circlePath)
        let shape = (polyPath) ? myMap.drawPolygonFromPath(this.map, polyPath, false) : myMap.drawCircleFromPath(this.map, circlePath, circleRadius, false);
        let pin = myMap.addPinToShape(this.map, shape, applicationType);
        pin.addListener('mouseover', function () { myMap.mouseover(card, shape, pin) });
        pin.addListener('mouseout', function () { myMap.mouseout(card, shape, pin) });
        pin.addListener('click', function () { _this.setInfoWindowContent(card, _this.infowindow, pin) });
        card.addEventListener('mouseover', function () { myMap.mouseover(card, shape, pin) });
        card.addEventListener('mouseout', function () { myMap.mouseout(card, shape, pin) });
        const zoomLink = card.querySelector('.zoom-link');
        if (zoomLink) {
            zoomLink.addEventListener('click', function (event) {
              stopLinkBeingFollowed(event);
              myMap.zoomToShape(_this.map, shape)
            }, false);
        }
        _this.mapShapes[appId] = {'pin': pin, 'shape': shape, 'card': card}
    }
  }

  hideAllMapShapes() {
    // console.log('=== hideAllMapShapes START ===')
    // console.log('MapShapes to hide:', Object.keys(this.mapShapes))
    for (let appId in this.mapShapes) {
      // console.log('Hiding shapes for appId:', appId)
      let mapShape = this.mapShapes[appId];
      if (mapShape['pin']) { // hide pins
        mapShape['pin'].setVisible(false);
        // console.log('  - Pin hidden for', appId)
      }
      if (mapShape['shape']) { // hide single shapes
        mapShape['shape'].setVisible(false);
        // console.log('  - Single shape hidden for', appId)
      }
      if (mapShape['shapes']) { // hide multi shapes
        mapShape['shapes'].forEach(shape => shape.setVisible(false));
        // console.log('  - Multiple shapes hidden for', appId)
      }
    }
    // console.log('=== hideAllMapShapes END ===')
  }

  // Properly destroy map shapes to prevent memory leaks
  destroyAllMapShapes() {
    // console.log('=== destroyAllMapShapes START ===')
    // console.log('MapShapes to destroy:', Object.keys(this.mapShapes))

    for (let appId in this.mapShapes) {
      // console.log('Destroying shapes for appId:', appId)
      let mapShape = this.mapShapes[appId];

      // Remove pins from map
      if (mapShape['pin']) {
        mapShape['pin'].setMap(null);
        // console.log('  - Pin destroyed for', appId)
      }

      // Remove single shapes from map
      if (mapShape['shape']) {
        mapShape['shape'].setMap(null);
        // console.log('  - Single shape destroyed for', appId)
      }

      // Remove multiple shapes from map
      if (mapShape['shapes']) {
        mapShape['shapes'].forEach(shape => {
          shape.setMap(null);
        });
        // console.log('  - Multiple shapes destroyed for', appId)
      }
    }

    // Clear the mapShapes object completely
    this.mapShapes = {};
    // console.log('MapShapes object cleared')
    // console.log('=== destroyAllMapShapes END ===')
  }

  // make all shapes visible, whether cards are visible or not
  drawMapShapesForAllCards() {
    for (let card of document.getElementsByClassName('card')) {
      let mapShape = this.mapShapes[card.dataset.appId]
      if (!mapShape) {
        console.warn(`No shape found for appId ${card.dataset.appId}`);
        continue; // skip if no shape is defined
      }

      if (mapShape['pin']) {
        mapShape['pin'].setVisible(true);
      }

      // Handle single shape
      if (mapShape['shape']) {
        mapShape['shape'].setVisible(true);
      }
      // Handle multiple shapes
      if (mapShape['shapes']) {
        mapShape['shapes'].forEach(shape => shape.setVisible(true));
      }

      this.activateShape(card.dataset.appId)
    }
  }

  // cards have been updated by changing tabs
  // which triggers a global event: window.dispatchEvent(new CustomEvent("tab-changed"))
  // this is handled by the dashboard controller which calls this function
  // clear them and redraw but leave rest of map unchanged
  updateAfterTabChange(event) {
    const tabHref = event.detail && event.detail.tabHref;
    const tabLink = event.detail && event.detail.target;

    // Main tab click (Flights/Cranes/WIP) — href ends with '-container'
    // Sub-tab click (location tabs) — anything else
    if (tabHref && tabHref.endsWith('-container')) {
      this.handleMainTabChange()
    } else {
      this.handleLocationTabChange(tabLink)
    }
  }

  handleMainTabChange() {
    const activeMainTab = document.querySelector('.tab-pane.active[id$="-container"]')
    if (!activeMainTab) return;

    // Reset location sub-tabs — use default sub-tab if configured, otherwise first
    const subTabs = activeMainTab.querySelectorAll('.nav-link[data-location-id]');
    if (subTabs.length > 0) {
      // Deactivate all sub-tabs and their panes
      subTabs.forEach(tab => {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
        const pane = document.querySelector(tab.getAttribute('href'));
        if (pane) {
          pane.classList.remove('show', 'active');
        }
      });

      // Find the default sub-tab for this main tab, or fall back to first
      let targetTab = subTabs[0];
      const defaultSubTabs = JSON.parse(this.data.get('defaultSubTabs') || '{}');
      const defaultTemplateId = defaultSubTabs[activeMainTab.id];
      if (defaultTemplateId) {
        const match = Array.from(subTabs).find(tab =>
          tab.getAttribute('href') === `#tab${defaultTemplateId}`
        );
        if (match) targetTab = match;
      }

      // Activate target sub-tab
      targetTab.classList.add('active');
      targetTab.setAttribute('aria-selected', 'true');
      const targetPane = document.querySelector(targetTab.getAttribute('href'));
      if (targetPane) {
        targetPane.classList.add('show', 'active');
      }
    }

    const activeSubTab = activeMainTab.querySelector('.nav-link.active[data-location-id]');
    const { lat: mapCentreLat, lon: mapCentreLon, zoom } = this.resolveMapCentre(activeSubTab, activeMainTab);

    const allLocationsCheckbox = document.getElementById('all-locations-toggle');
    if (allLocationsCheckbox) allLocationsCheckbox.checked = false;

    this.applyTabDefaults(activeMainTab.id, mapCentreLat, mapCentreLon, zoom)
    this.filterApplicationsToActiveLocation()
  }

  handleLocationTabChange(tabLink) {
    const activeMainTab = document.querySelector('.tab-pane.active[id$="-container"]');
    if (!activeMainTab) return;

    const { lat, lon, zoom } = this.resolveMapCentre(tabLink, activeMainTab);

    // Uncheck "All Locations" checkbox (same as handleMainTabChange)
    const allLocationsCheckbox = document.getElementById('all-locations-toggle');
    if (allLocationsCheckbox) allLocationsCheckbox.checked = false;

    this.applyTabDefaults(activeMainTab.id, lat, lon, zoom);
  }

  // Filter map shapes to only show applications belonging to the active location sub-tab
  filterApplicationsToActiveLocation() {
    const visibleAppTypes = this.getVisibleApplicationTypes();

    for (let appId in this.mapShapes) {
      const card = this.mapShapes[appId].card;
      const cardAppType = this.getApplicationTypeFromCard(card);
      const appTypeVisible = visibleAppTypes.includes(cardAppType);
      this.setApplicationVisibility(appId, this.shouldShowShape(card, appTypeVisible));
    }
  }

  // Get the assessment_template_id from the active sub-tab within the active main tab
  getActiveLocationId() {
    const activeMainTab = document.querySelector('.tab-pane.active[id$="-container"]');
    if (!activeMainTab) return null;

    const activeSubTab = activeMainTab.querySelector('.nav-link.active[data-location-id]');
    if (!activeSubTab) return null;

    return activeSubTab.dataset.locationId;
  }

  // Returns array of currently checked application type names
  getVisibleApplicationTypes() {
    const types = [];
    const checkboxMap = { 'flights-toggle': 'flights', 'cranes-toggle': 'cranes', 'wip-toggle': 'wip' };
    for (const [id, name] of Object.entries(checkboxMap)) {
      const checkbox = document.getElementById(id);
      if (checkbox && checkbox.checked) types.push(name);
    }
    return types;
  }

  // Handler for "All locations" checkbox
  toggleAllLocations(event) {
    if (event.target.checked) {
      this.showAllLocationApplications();
    } else {
      this.filterApplicationsToActiveLocation();
    }
  }

  // Show shapes filtered by app type only, ignoring location
  showAllLocationApplications() {
    const visibleAppTypes = this.getVisibleApplicationTypes();

    for (let appId in this.mapShapes) {
      const mapShape = this.mapShapes[appId];
      const card = mapShape.card;
      const cardAppType = this.getApplicationTypeFromCard(card);
      const visible = visibleAppTypes.includes(cardAppType);
      this.setApplicationVisibility(appId, visible);
    }
  }

  async applyTabDefaults(tabId, mapCentreLat, mapCentreLon, zoom = null) {
    const cam = this.getTabCameraDefaults(tabId);
    const effectiveZoom = zoom || cam.zoom;
    this.map.setOptions(defaultMapOptions(mapCentreLat, mapCentreLon, effectiveZoom, cam.mapType));

    const defaults = this.getTabDefaultsFromDOM(tabId);

    this.hideAllLayers();
    this.hideAllApplications();
    await layerManager.resetDefaultLayerVisibility();

    if (defaults) {
      await Promise.all(defaults.layers.map(layerId => {
        return this.setLayerVisibility(layerId, true);
      }));
    }

    if (defaults) {
      defaults.applications.forEach(appType => {
        this.syncApplicationTypeLayerVisibility(appType, true);
      });
      this.syncApplicationCheckboxesForTab(defaults.applications);
    }

    if (this.data.get('multiSite') === 'true') {
      this.filterApplicationsToActiveLocation();
    }
  }

  syncApplicationCheckboxesForTab(visibleApplications) {
    // Update individual application checkboxes
    const applicationTypes = ['flights', 'cranes', 'wip'];
    applicationTypes.forEach(appType => {
      const checkbox = document.getElementById(`${appType}-toggle`);
      if (checkbox) {
        checkbox.checked = visibleApplications.includes(appType);
      }
    });

    // Update "show all" checkbox - only checked if all visible app types are checked
    const showAllCheckbox = document.getElementById('show-all-toggle');
    if (showAllCheckbox) {
      // Check if all checkboxes that exist are checked
      const existingCheckboxes = applicationTypes
        .map(appType => document.getElementById(`${appType}-toggle`))
        .filter(checkbox => checkbox !== null);

      const allChecked = existingCheckboxes.length > 0 &&
        existingCheckboxes.every(checkbox => checkbox.checked);
      showAllCheckbox.checked = allChecked && visibleApplications.length > 0;
    }
  }

  hideAllLayers() {
    // Hide all overlay layers
    const overlayLayers = ['restricted-airspace', 'aaz_drone', 'aaz_crane', 'crane-6km-zones'];
    overlayLayers.forEach(layerId => {
      this.setLayerVisibility(layerId, false);
    });
  }

  hideAllApplications() {
    // Hide all application types
    const applicationTypes = ['flights', 'cranes', 'wip'];
    applicationTypes.forEach(appType => {
      this.syncApplicationTypeLayerVisibility(appType, false);
    });
  }

  syncApplicationTypeLayerVisibility(appType, visible) {
    const checkbox = document.getElementById(`${appType}-toggle`);
    if (checkbox && checkbox.checked !== visible) {
      checkbox.checked = visible;
      // Directly call toggleApplicationType to actually show/hide the applications
      this.toggleApplicationType(appType, visible);
    }
  }

  async setLayerVisibility(layerId, visible) {
    if (layerManager) {
      const currentlyVisible = layerManager.isLayerVisible(layerId);
      if (currentlyVisible !== visible) {
        await layerManager.toggleLayer(layerId);
      }
    }
  }

  recentre(event) {
    // console.log('recentre')
    stopLinkBeingFollowed(event)
    let coords = JSON.parse(event.currentTarget.dataset.coords)
    this.map.setCenter({lat: coords[0], lng: coords[1]})
  }

  // Resolves the map centre coordinates and per-template zoom to use.
  // Starts with the org-level fallback (data-dashboard-map-map-centre-lat/lon),
  // then overrides with the given tab element's data-coords/data-zoom if available.
  // tabPaneElement is used for tab-level zoom fallback when no sub-tab zoom is set.
  resolveMapCentre(tabElement = null, tabPaneElement = null) {
    let lat = this.data.get("mapCentreLat");
    let lon = this.data.get("mapCentreLon");
    let zoom = null;
    if (tabElement) {
      if (tabElement.dataset.coords) {
        try {
          const coords = JSON.parse(tabElement.dataset.coords);
          if (coords[0] && coords[1]) {
            lat = coords[0];
            lon = coords[1];
          }
        } catch (e) {}
      }
      if (tabElement.dataset.zoom) { zoom = parseInt(tabElement.dataset.zoom); }
    }
    if (!zoom && tabPaneElement) { zoom = parseInt(tabPaneElement.dataset.defaultZoom); }
    if (!zoom) { zoom = parseInt(this.data.get('defaultZoom')) || 12; }
    return { lat, lon, zoom };
  }

  async connect() {
    console.log('dashboard map controller connected')
    this.mapShapes = {}

    const activeMainTab = document.querySelector('.tab-pane.active[id$="-container"]');
    const activeSubTab = activeMainTab
      ? activeMainTab.querySelector('.nav-link.active[data-location-id]')
      : null;
    const { lat: mapCentreLat, lon: mapCentreLon, zoom } = this.resolveMapCentre(activeSubTab, activeMainTab);

    // Camera defaults from DOM data attributes
    const cam = this.getTabCameraDefaults(activeMainTab ? activeMainTab.id : null);
    const initialOptions = defaultMapOptions(mapCentreLat, mapCentreLon, zoom, cam.mapType);

    let map = new google.maps.Map(this.mapTarget, initialOptions);
    this.map = map
    await layerManager.initializeLayers(this.map, this);  // reads correct data-visible from server

    myMap.addGetBoundsFunction();
    this.infowindow = new google.maps.InfoWindow({content: ""})

    if (this.hasInfoPanelTarget && this.hasInfoPanelContentTarget) {
      mapInfoPanel.setup(this, this.infoPanelTarget, this.infoPanelContentTarget, this.map);
    }

    this.setupMapAndCards();

    if (activeMainTab) {
      // Show application shapes matching the server-rendered checkbox states
      this.applyApplicationVisibilityFromCheckboxes();
      if (this.data.get('multiSite') === 'true') {
        this.filterApplicationsToActiveLocation();
      }
    } else {
      this.drawMapShapesForAllCards();
      this.syncCheckboxStates();
    }
  }

  // Apply application visibility based on current checkbox states (used on initial load)
  applyApplicationVisibilityFromCheckboxes() {
    const applicationTypes = ['flights', 'cranes', 'wip'];
    applicationTypes.forEach(appType => {
      const checkbox = document.getElementById(`${appType}-toggle`);
      if (checkbox) {
        this.toggleApplicationType(appType, checkbox.checked);
      }
    });
  }

  // Sync checkbox states with layer manager visibility states
  // Note: Layer synchronization is now handled automatically by LayerManager.updateAllToggleUI()
  syncCheckboxStates() {
    // Sync application layer checkboxes - all applications are shown by default
    const applicationCheckboxes = document.querySelectorAll('[data-action*="toggleApplicationLayer"]');
    applicationCheckboxes.forEach(checkbox => {
      // All applications are visible initially
      checkbox.checked = true;
    });

    // Layer checkboxes are now automatically synchronized by LayerManager
    // when layer states change, so no manual sync needed here
  }

  // Info panel methods
  closeInfoPanel() {
    mapInfoPanel.close();
  }

}
